import {useCallback} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Checkbox,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';

function handleOptionalAddOnCheckboxKeyDown(event, isDisabled, onToggle) {
  if (isDisabled) return;

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }
}

/**
 * Stacked optional add-on item — single-select list with toggle-off support (content section only).
 * Used when a slot has 2+ visible products.
 * Header is rendered by parent OptionalAddOnGroup.
 *
 * Displays:
 * ┌──────────────────────────────────────────────────────┐
 * │ [image]  product name   See details (mobile) [checkbox] │  ← row × N
 * └──────────────────────────────────────────────────────┘
 *
 * @param {Object} props
 * @param {Array<{productId: string, name: string, price: number|null, imageUrl: string|null}>} props.products
 * @param {string|null} [props.selectedProductId]
 * @param {boolean} [props.isDisabled]
 * @param {Function} props.onChange - (productId: string|null) — null means deselect
 * @param {Function} [props.onDetailsClick] - (productId: string)
 */
const OptionalAddOnStacked = ({
  products,
  selectedProductId,
  isDisabled = false,
  onChange,
  onDetailsClick,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('OptionalAddOns', {});
  const {currency} = useCurrency();

  const handleChange = useCallback((productId) => {
    onChange(selectedProductId === productId ? null : productId);
  }, [onChange, selectedProductId]);

  const handleDetailsClick = useCallback((productId) => {
    onDetailsClick?.(productId);
  }, [onDetailsClick]);

  return (
    <Box className="optional-add-on-stacked__root">
      {products.map((product) => {
        const isChecked = product.productId === selectedProductId;
        const checkboxId = `optional-add-on-stacked-${product.productId}`;
        const priceLabel = product.price != null ?
          formatCurrencyHideTrailingZeroCents(intl, product.price, currency) :
          null;

        return (
          <Box
            key={product.productId}
            className="optional-add-on-stacked__item"
            {...styles.item}
            {...sfccProductIdDataAttrs('option', product.productId)}
          >
            <Box className="optional-add-on-stacked__item-image-wrap" {...styles.itemImageWrap}>
              {product.imageUrl ? (
                <Image
                  alt={product.name}
                  loading="lazy"
                  src={product.imageUrl}
                  {...styles.itemImage}
                />
              ) : null}
            </Box>

            <Box className="optional-add-on-stacked__meta" {...styles.itemMeta}>
              <Text
                as="label"
                className="optional-add-on-stacked__label"
                htmlFor={checkboxId}
                {...styles.itemLabel}
              >
                {product.name}
              </Text>
              {onDetailsClick && (
                <Box
                  as="button"
                  type="button"
                  {...styles.detailsLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDetailsClick(product.productId);
                  }}
                >
                  {intl.formatMessage({
                    defaultMessage: 'See details',
                    id: 'optional_add_ons.see_details',
                  })}
                </Box>
              )}
            </Box>

            {priceLabel && (
              <Text className="optional-add-on-stacked__price" {...styles.itemPrice}>
                +{priceLabel}
              </Text>
            )}

            {/* Chakra Checkbox doesn't forward data-* to the native <input>,
                so productId is passed as an argument rather than via dataset */}
            <Checkbox
              className="optional-add-on-stacked__checkbox"
              id={checkboxId}
              isChecked={isChecked}
              isDisabled={isDisabled}
              onChange={() => handleChange(product.productId)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => handleOptionalAddOnCheckboxKeyDown(
                event,
                isDisabled,
                () => handleChange(product.productId)
              )}
              {...styles.itemCheckbox}
            />
          </Box>
        );
      })}
    </Box>
  );
};

OptionalAddOnStacked.displayName = 'OptionalAddOnStacked';

OptionalAddOnStacked.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number,
      imageUrl: PropTypes.string,
    })
  ).isRequired,
  selectedProductId: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDetailsClick: PropTypes.func,
};

export default OptionalAddOnStacked;
